import { html } from '/vendor/htm-preact.module.js';
import { Icon } from '/icons.js';
import { useRead } from '/read.js';
import { RowSkeleton } from '/skeleton.js';

/**
 * Matches already played.
 *
 * Yours, or one circle's. The list says who, what it finished, and whether
 * there is anything to look at shot by shot — a row that opens onto nothing is
 * a row somebody taps twice before believing it.
 */
export function MatchesScreen({ circleId, onGo, onDone }) {
  const path = circleId
    ? `/api/matches?circle_id=${encodeURIComponent(circleId)}`
    : '/api/matches';
  const [matches, error] = useRead(path);

  if (error) {
    return html`
      <div class="screen">
        <button class="secondary" onClick=${onDone}>Back</button>
        <p class="error" data-testid="history-error">
          ${error.detail || error.message}
        </p>
      </div>`;
  }

  return html`
    <div class="rowbetween">
      <h2>${circleId ? 'Circle history' : 'Your matches'}</h2>
      <button class="secondary" style="width:auto"
              data-testid="history-back" onClick=${onDone}>Back</button>
    </div>

    ${matches === undefined
      ? html`<div class="stack" data-testid="history-waiting">
               <${RowSkeleton} /><${RowSkeleton} /><${RowSkeleton} />
             </div>`
      : matches.length === 0
        ? html`<p class="empty" data-testid="history-empty">
                 No matches yet.
               </p>`
        : html`
          <div class="stack" data-testid="history-list">
            ${matches.map((m) => {
              const [a, b] = m.players;
              const on = m.status === 'in_progress';
              return html`
                <button key=${m.match_id} class="secondary card"
                        data-testid=${`history-${m.match_id}`}
                        onClick=${() => onGo('live', m.match_id)}>
                  <span class="rowbetween" style="width:100%">
                    <span>
                      <span style="display:block">
                        ${`${a.name} v ${b.name}`}
                      </span>
                      <span class="sub" style="text-transform:none">
                        ${when(m.played_at)}
                        ${m.shot_by_shot ? ' · shot by shot' : ''}
                        ${on ? ' · still playing' : ''}
                      </span>
                    </span>
                    <span class="mono" data-testid="history-score">
                      ${`${a.frames}–${b.frames}`}
                    </span>
                  </span>
                </button>`;
            })}
          </div>`}`;
}

/** The day, not the second. Nobody is looking for 19:42:07. */
function when(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined,
    { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * A finished frame, shot by shot.
 *
 * Only frames scored that way have anything here — a one-tap frame never saw
 * the visits, and saying "no shots" for it would read as though something had
 * been lost rather than never recorded.
 */
export function Frames({ live }) {
  const byFrame = live.shots_by_frame || {};
  const played = live.frames || [];
  if (!played.length) return null;

  const nameOf = (id) =>
    (live.players.find((p) => p.user_id === id) || {}).name || 'Someone';

  return html`
    <div class="stack" data-testid="match-frames">
      <label>Frame by frame</label>
      ${played.map((winner, i) => {
        const n = i + 1;
        const shots = byFrame[String(n)] || [];
        return html`
          <div class="card stack" key=${n} data-testid=${`frame-${n}`}>
            <div class="rowbetween">
              <span class="mono">${`FRAME ${n}`}</span>
              <span data-testid=${`frame-${n}-winner`}>
                ${`${nameOf(winner)} won it`}
              </span>
            </div>
            ${shots.length > 0
              ? html`
                <div class="log" data-testid=${`frame-${n}-shots`}>
                  ${shots.map((s, j) => html`
                    <div class="card" key=${j}>
                      <span class="who">${`${j + 1}`}</span>
                      <span>${describeShot(s)}</span>
                    </div>`)}
                </div>`
              : html`<p class="sub" data-testid=${`frame-${n}-notaped`}>
                       Scored in one tap, so there are no shots to show.
                     </p>`}
          </div>`;
      })}
    </div>`;
}

const SHOT_WORDS = {
  pot: 'Balls down', miss: 'Nothing down', foul: 'Foul',
  whitedown: 'White down', safe: 'Safety', illegal: 'Illegal break',
  rerack: 'Re-rack',
};

function describeShot(shot) {
  const word = SHOT_WORDS[shot.id] || shot.id;
  const potted = shot.potted || {};
  const bits = [];
  if (potted.red) bits.push(`${potted.red} red`);
  if (potted.yellow) bits.push(`${potted.yellow} yellow`);
  if (potted.black) bits.push('black');
  return bits.length ? `${word} — ${bits.join(', ')}` : word;
}
